import { Tooltip, TooltipTrigger } from "@artis/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBracketRound,
  faBracketRoundRight,
  faDivide,
  faMinus,
  faPlus,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { useMemo, type ReactNode } from "react";

import type { TProduct } from "../../../../data";
import {
  type Expression,
  type Op,
  type RoundDirection,
  callDirectionToRoundDirection,
  getNegativeNumberLiteral,
  shouldWrapExpression,
  useParsedFormula,
} from "../../../utils/formula-ast";

function OperatorChip({ op }: { op: Op }) {
  const icon = () => {
    switch (op) {
      case "Add":
        return faPlus;
      case "Subtract":
        return faMinus;
      case "Multiply":
        return faXmark;
      case "Divide":
        return faDivide;
    }
  };

  return (
    <div className="text-[14px] font-bold mx-[4.5px] text-[var(--color-neutral)]">
      <FontAwesomeIcon icon={icon()} />
    </div>
  );
}

function Parenthesis({ open }: { open: boolean }) {
  return (
    <div className="text-[14px] font-bold mx-[4.5px] text-[var(--color-neutral)]">
      <FontAwesomeIcon icon={open ? faBracketRound : faBracketRoundRight} />
    </div>
  );
}

function ManualChip() {
  return (
    <div className="px-[9px] py-[1.5px] rounded-xl text-[10.5px] font-normal bg-[var(--color-white)] text-[var(--color-black)]">
      MANUAL
    </div>
  );
}

function NumberChip({ value }: { value: number }) {
  return (
    <div className="px-[9px] py-[1.5px] rounded-xl text-[10.5px] font-normal bg-[var(--color-formula-value)] text-[var(--color-black)]">
      {value}
    </div>
  );
}

function RowIndexChip({ offset }: { offset: number }) {
  if (offset === 0) return null;

  const sign = offset > 0 ? "+" : "";
  const displayOffset = `${sign}${offset}`;

  return (
    <TooltipTrigger>
      <div className="px-[9px] pt-[1px] pb-[2px] rounded-xl text-[10.5px] font-normal bg-[var(--color-formula-plus)] text-[var(--color-black)]">
        [{displayOffset}]
      </div>
      <Tooltip>
        Numbers in brackets indicate an offset to the currently referenced period, e.g. [-1] on M3 would reference M2
        instead.
      </Tooltip>
    </TooltipTrigger>
  );
}

function ProductChip({
  productId,
  field,
  offset,
  products,
  fetching,
}: {
  productId: string;
  field: string;
  offset: number;
  products: TProduct[];
  fetching: boolean;
}) {
  const product = useMemo(() => products.find((p) => p.id === productId), [products, productId]);

  const displayName = useMemo(() => {
    if (fetching) return "Loading...";
    if (!product) return "MISSING PERMISSION";
    const regex = /\s*\([^)]*\)/g;
    return product.name.replace(regex, "");
  }, [fetching, product]);

  const title = useMemo(() => {
    if (!product) return `Product ID: ${productId}`;
    return `Curve: ${product.name}\nField: ${field}\nPackage: ${product.packageByPackage?.name || "N/A"}`;
  }, [product, productId, field]);

  const hasPackage = product?.packageByPackage?.name;
  const bgColor = hasPackage || fetching ? "var(--color-formula-curve)" : "red";

  return (
    <div className="flex gap-1">
      <TooltipTrigger>
        <div className="inline-flex text-[10.5px] font-normal text-[var(--color-black)] whitespace-nowrap">
          <span className="rounded-xl px-[9px] py-[1.5px] z-1" style={{ backgroundColor: bgColor }}>
            {displayName}
          </span>
          <span
            className="rounded-r-xl pl-[12px] pr-[9px] py-[1.5px] ml-[-9px]"
            style={{ backgroundColor: `color-mix(in srgb, ${bgColor} 60%, white 40%)` }}
          >
            .{field}
          </span>
        </div>
        <Tooltip>
          <span className="whitespace-pre-line">{title}</span>
        </Tooltip>
      </TooltipTrigger>
      <RowIndexChip offset={offset} />
    </div>
  );
}

const roundKeywordClass =
  "text-[10.5px] [color:var(--colour-text-default,#000)] not-italic [font-weight:var(--type-font-weight-medium,500)] [line-height:var(--typography-body-line-height,20px)] [letter-spacing:var(--typography-letter-spacing-default,0)]";

function RoundLabel() {
  return <span className={roundKeywordClass}>ROUND</span>;
}

function Separator() {
  return <span className="text-[13px] font-bold text-foreground">,</span>;
}

function DirectionLabel({ direction }: { direction: RoundDirection }) {
  return <span className={roundKeywordClass}>{direction.toUpperCase()}</span>;
}

function ExpressionRenderer({
  expr,
  products,
  fetching,
  parentOp,
  explicitParenKeys,
}: {
  expr: Expression;
  products: TProduct[];
  fetching: boolean;
  parentOp?: Op;
  explicitParenKeys: ReadonlySet<string>;
}) {
  const wrap = (content: ReactNode) => {
    if (!shouldWrapExpression(expr, parentOp, explicitParenKeys)) {
      return content;
    }
    return (
      <>
        <Parenthesis open={true} />
        {content}
        <Parenthesis open={false} />
      </>
    );
  };

  if ("Manual" in expr) {
    return wrap(<ManualChip />);
  }

  if ("Value" in expr) {
    return wrap(<NumberChip value={expr.Value} />);
  }

  if ("Variable" in expr) {
    const { product, field, offset } = expr.Variable;
    return wrap(
      <ProductChip productId={product} field={field} offset={offset} products={products} fetching={fetching} />,
    );
  }

  if ("UnaryOp" in expr) {
    const negativeLiteral = getNegativeNumberLiteral(expr);
    if (negativeLiteral !== undefined) {
      return <NumberChip value={negativeLiteral} />;
    }

    return wrap(
      <>
        <OperatorChip op="Subtract" />
        <ExpressionRenderer
          expr={expr.UnaryOp}
          products={products}
          fetching={fetching}
          explicitParenKeys={explicitParenKeys}
        />
      </>,
    );
  }

  if ("BinaryOp" in expr) {
    const { lhs, op, rhs } = expr.BinaryOp;

    return wrap(
      <>
        <ExpressionRenderer
          expr={lhs}
          products={products}
          fetching={fetching}
          parentOp={op}
          explicitParenKeys={explicitParenKeys}
        />
        <OperatorChip op={op} />
        <ExpressionRenderer
          expr={rhs}
          products={products}
          fetching={fetching}
          parentOp={op}
          explicitParenKeys={explicitParenKeys}
        />
      </>,
    );
  }

  if ("Call" in expr) {
    const [funcName, args] = expr.Call;
    if (funcName === "Round" && args[0] && args[1] && "Value" in args[1]) {
      const inner = args[0];
      const multiple = args[1].Value;
      const direction = args[2] && "Value" in args[2] ? callDirectionToRoundDirection(args[2].Value) : undefined;
      return (
        <>
          <RoundLabel />
          <Parenthesis open={true} />
          <ExpressionRenderer
            expr={inner}
            products={products}
            fetching={fetching}
            explicitParenKeys={explicitParenKeys}
          />
          <Separator />
          <NumberChip value={multiple} />
          {direction && (
            <>
              <Separator />
              <DirectionLabel direction={direction} />
            </>
          )}
          <Parenthesis open={false} />
        </>
      );
    }
    return null;
  }

  return null;
}

export function FormulaDisplay({
  fetching,
  products,
  formulaString,
}: {
  fetching: boolean;
  products: TProduct[];
  formulaString: string;
}) {
  const { ready, ast, explicitParenKeys } = useParsedFormula(formulaString);

  const content = useMemo(() => {
    if (!ready) return <span>Loading parser...</span>;
    if (!ast) return <span>Invalid formula</span>;
    return (
      <ExpressionRenderer expr={ast} products={products} fetching={fetching} explicitParenKeys={explicitParenKeys} />
    );
  }, [ready, ast, explicitParenKeys, products, fetching]);

  return (
    <div className="flex flex-wrap font-bold text-xs text-foreground items-center gap-2">
      {content}
      <div className="min-w-2 min-h-2" />
    </div>
  );
}
